import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';

const UploadModalContext = createContext({
  showUploadModal: false,
  openUploadModal: () => {},
  closeUploadModal: () => {},
  handleGetStartedClick: () => {},
  showLoginModal: false,
  openLoginModal: () => {},
  closeLoginModal: () => {}
});

export const useUploadModal = () => {
  const context = useContext(UploadModalContext);
  if (!context) {
    throw new Error('useUploadModal must be used within UploadModalProvider');
  }
  return context;
};

export const UploadModalProvider = ({ children }) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingUploadOpen, setPendingUploadOpen] = useState(false);
  const { currentUser } = useAuth();

  // Open upload modal directly
  const openUploadModal = () => {
    setShowUploadModal(true);
  };

  // Close upload modal and reset pending state
  const closeUploadModal = () => {
    setShowUploadModal(false);
    setPendingUploadOpen(false);
  };

  // Open login modal
  const openLoginModal = () => {
    setShowLoginModal(true);
  };

  // Close login modal
  const closeLoginModal = () => {
    setShowLoginModal(false);
  };

  // Handle "Get Started" button click
  const handleGetStartedClick = () => {
    if (currentUser) {
      // If user is signed in, open upload modal directly
      openUploadModal();
    } else {
      // If user is not signed in, set pending flag and open login modal
      setPendingUploadOpen(true);
      openLoginModal();
    }
  };

  // Auto-open upload modal after successful sign-in if pending
  useEffect(() => {
    if (currentUser && pendingUploadOpen) {
      openUploadModal();
      setPendingUploadOpen(false);
      closeLoginModal();
    }
  }, [currentUser, pendingUploadOpen]);

  const value = {
    showUploadModal,
    openUploadModal,
    closeUploadModal,
    handleGetStartedClick,
    showLoginModal,
    openLoginModal,
    closeLoginModal
  };

  return (
    <UploadModalContext.Provider value={value}>
      {children}
    </UploadModalContext.Provider>
  );
};